import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { checkDateFormat } from '@app/core/helpers/tools';
import { DefaultApiType, IChosenSortValue } from '@app/core/models/core-models';
import { IStatisticsTableColumn } from '@app/core/services/statistics/statistics.types';
import { environment } from '@env/environment';
import { CampaignsStatisticsService } from '@pages/statistics/pages/campaigns-statistics/campaigns-statistics.service';
import { ConfigurationsStatisticsService } from '@pages/statistics/pages/configurations/configurations-statistics.service';
import { CountriesStatisticsService } from '@pages/statistics/pages/countries/countries-statistics.service';
import { StatsRequestParams } from '@pages/statistics/shared/interfaces/statistics';
import { HidelinkStatisticsService } from '@pages/statistics/pages/hidelink/hidelink-statistics.service';
import { IvrStatisticsService } from '@pages/statistics/pages/ivr-stats/ivr-statistics.service';
import { LeadsStatisticsService } from '@pages/statistics/pages/leads/leads-statistics.service';
import { PpiStatisticsService } from '@pages/statistics/pages/ppi/ppi-statistics.service';
import { SmartlinksStatisticsService } from '@pages/statistics/pages/smartlinks/smartlinks-statistics.service';
import { VisitsStatisticsService } from '@pages/statistics/pages/visits/visits-statistics.service';
import { AvailableStatisticsTableColumns, TableService } from '@pages/statistics/shared/statistics-table/table.service';
import { isArray, isEmpty } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { DailyStatisticsService } from '@pages/statistics/pages/daily/daily-statistics.service';

export type AvailableReportsFormats = 'json' | 'xml';

export type ExportToFileFilterParams = Omit<StatsRequestParams, 'order_field' | 'order_type' | 'page' | 'per_page'> & {
  countries?: string[];
  program_id?: string[];
};

export interface ExportReportConfig {
  type: AvailableReportsTypes;
  format: AvailableReportsFormats;
  table_columns: string[];
  filter_columns: ExportToFileFilterParams;
  order_field?: string[];
  delete: boolean;
}

export type AvailableReportsTypes =
  | 'programs'
  | 'leads'
  | 'visits'
  | 'configurations'
  | 'daily'
  | 'countries'
  | 'referrals'
  | 'smartlinks'
  | 'incent'
  | 'ppi'
  | 'ivr'
  | 'hidelinks';

const baseColumnsNamesMap = {
  lead: ['leads', 'leads_status_payment_waiting', 'leads_status_rejected', 'leads_status_wait'],
  earnings: ['earnings_status_payment_waiting', 'earnings_status_rejected', 'earnings_status_to_payment', 'earnings_status_wait']
};

export interface SingleStatisticsReport {
  id: number;
  name: string;
  created_at: string;
  file_type: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportReportsService implements OnDestroy {
  public currentStatisticsSection$ = new BehaviorSubject<AvailableReportsTypes>('daily');
  public tableColumns$: Observable<IStatisticsTableColumn[]>;

  public columnsToIgnore: AvailableStatisticsTableColumns[] = ['leadToPay'];

  private destroy$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly statisticsTableService: TableService,
    private readonly campaignsStatisticsService: CampaignsStatisticsService,
    private readonly configurationsStatisticsService: ConfigurationsStatisticsService,
    private readonly leadsStatisticsService: LeadsStatisticsService,
    private readonly visitsStatisticsService: VisitsStatisticsService,
    private readonly dailyStatisticsService: DailyStatisticsService,
    private readonly countriesStatisticsService: CountriesStatisticsService,
    private readonly smartlinksStatisticsService: SmartlinksStatisticsService,
    private readonly ppiStatisticsService: PpiStatisticsService,
    private readonly ivrStatisticsService: IvrStatisticsService,
    private readonly hidelinksStatisticsService: HidelinkStatisticsService
  ) {
    this.tableColumns$ = this.statisticsTableService.tableColumns$.pipe(
      map(tableColumns => {
        return tableColumns
          .map(({ results }) => results)
          .flat()
          .filter(column => !this.columnsToIgnore.includes(column.column));
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setCurrentStatisticsSection = (type: AvailableReportsTypes) => {
    this.currentStatisticsSection$.next(type);
  };

  public getAvailableColumnsForExport = (type: AvailableReportsTypes): Observable<string[]> => {
    const params = { type };

    return this.http.get<DefaultApiType<string[]>>(environment.apiUrl + '/statistics/export/columns', { params }).pipe(map(({ data }) => data ?? []));
  };

  public downloadReport = (fileName: string): Observable<HttpResponse<Blob>> => {
    const params = { file_name: fileName };

    return this.http.get(environment.apiUrl + '/statistics/export/download', {
      responseType: 'blob',
      observe: 'response',
      params
    });
  };

  public deleteReport = (fileName: string) => {
    const params = { file_name: fileName };

    return this.http.delete<DefaultApiType<{ message?: string }>>(environment.apiUrl + '/statistics/export/delete', { params });
  };

  public getReportsList = (type: AvailableReportsTypes) => {
    return this.http.get<DefaultApiType<{ reports: SingleStatisticsReport[] }>>(environment.apiUrl + `/statistics/export/reports_list?type=${type}`);
  };

  public getAvailableReportsFormats = () => {
    return this.http.get<DefaultApiType<AvailableReportsFormats[]>>(environment.apiUrl + `/statistics/export/formats`);
  };

  public exportReport = (exportReportConfig: ExportReportConfig) => {
    return this.http.post<DefaultApiType<{ message?: string }>>(environment.apiUrl + `/statistics/export/dispatch`, { ...exportReportConfig });
  };

  public prepareExportReportConfig = (requestParams: StatsRequestParams, sortMethod: IChosenSortValue, tableColumns: string[]) => {
    let filter_columns: ExportToFileFilterParams = {
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };
    let table_columns: string[] = [];
    let order_field: string[] = [];

    if (!isEmpty(sortMethod) && sortMethod.orderField && sortMethod.orderType) {
      const { orderField, orderType } = sortMethod;

      order_field = [orderField, orderType];
    }

    if (!isEmpty(requestParams)) {
      const { order_field, order_type, page, per_page, ['countries[]']: countries, ['programs[]']: programs, ...requestParamsNew } = requestParams;

      filter_columns = { ...requestParamsNew, countries: countries, ...filter_columns };

      if (programs)
        filter_columns =
          Array.isArray(programs) && programs.length > 0 ? { ...filter_columns, program_id: [...programs] } : { ...filter_columns, program_id: [programs] };
    }

    if (isArray(tableColumns) && !isEmpty(tableColumns)) {
      table_columns = [...tableColumns.filter(val => !isEmpty(val))];
    }

    // filter_columns.per_page && delete filter_columns.per_page;

    return {
      filter_columns,
      table_columns,
      order_field
    };
  };
}
